// src/components/generics/LoginModal.jsx
import React, { useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { loginUser } from "../../redux/actions/userActions";
import axiosInstance from "../../utils/AxiosInstance";
import {
  clearPendingVerificationEmail,
  getPendingVerificationEmail,
  setPasswordResetEmail,
  setPendingVerificationEmail,
  setSelectedPlan,
} from "../../utils/authStorage";

import TermsModal from "./TermsOfUse";
import PrivacyPolicy from "./PrivacyPolicy";

const LoginModal = ({ onClose, onLoginSuccess, onGoToSignUp, selectedPlan }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    email: "",
    password: "",
    stayLoggedIn: false,
  });
  const [errorMessage, setErrorMessage] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [pendingVerificationEmail] = useState(() => getPendingVerificationEmail());

  const [isTermsOpen, setIsTermsOpen] = useState(false);
  const [isPrivacyOpen, setIsPrivacyOpen] = useState(false);

  const isUnverifiedAccountMessage = (message) => {
    const text = String(message || "").toLowerCase();
    return (
      text.includes("not yet been activated") ||
      text.includes("not activated") ||
      text.includes("inactive") ||
      text.includes("activate your account")
    );
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const buildUser = (data) => ({
    id: data?.id,
    name: data?.name,
    email: data?.email,
    type: data?.type,
    account: data?.account,
    credits: data?.account?.credit,
    buyDrawActiveMegaMillions: data?.buyDrawActiveMegaMillions,
    buyDrawActivePowerBall: data?.buyDrawActivePowerBall,
  });

  const notifyLoggedIn = (user) => {
    window.dispatchEvent(
      new CustomEvent("auth:changed", {
        detail: { authenticated: true, source: "login-modal", user },
      })
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMessage("");
    setIsSubmitting(true);

    try {
      const loginResponse = await axiosInstance.post("/security/auth/login", {
        email: formData.email,
        password: formData.password,
        stayLoggedIn: formData.stayLoggedIn,
      });

      const user = buildUser(loginResponse.data);

      // Auth itself lives in HttpOnly cookies set by the server.
      // Validate that we got a real user payload back.
      if (!user.id) {
        throw new Error("Login succeeded, but the backend did not return a valid user.");
      }

      clearPendingVerificationEmail();
      dispatch(loginUser(user));
      notifyLoggedIn(user);

      if (selectedPlan?.key) {
        setSelectedPlan(selectedPlan.key, { persist: Boolean(formData.stayLoggedIn) });
      } else {
        setSelectedPlan(null);
      }

      // Tell the parent first (so it can flip its own state),
      // then close the modal.
      if (typeof onLoginSuccess === "function") {
        onLoginSuccess(user);
      }
      if (typeof onClose === "function") {
        onClose();
      }
    } catch (err) {
      const status = err?.response?.status;
      const backendMessage =
        typeof err?.response?.data === "string"
          ? err.response.data
          : err?.response?.data?.message || err?.response?.data?.error;
      const normalizedEmail = String(formData.email || "").trim().toLowerCase();

      if (normalizedEmail && isUnverifiedAccountMessage(backendMessage)) {
        setPendingVerificationEmail(normalizedEmail);
        if (typeof onClose === "function") onClose();
        navigate("/verification", {
          state: { email: normalizedEmail, operation: "verification" },
        });
        return;
      }

      if (status === 401 || status === 403) {
        setErrorMessage("Invalid username or password. Please try again.");
      } else {
        setErrorMessage(backendMessage || err?.message || "Login failed. Please try again.");
      }

      setTimeout(() => setErrorMessage(""), 3500);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleForgotPassword = () => {
    const email = (formData.email || "").trim();
    if (!email) {
      setErrorMessage("Please enter your email before resetting password.");
      setTimeout(() => setErrorMessage(""), 3000);
      return;
    }
    setPasswordResetEmail(email);
    if (typeof onClose === "function") onClose();
    navigate("/verification", { state: { email, operation: "password-reset" } });
  };

  const handleContinueVerification = () => {
    if (!pendingVerificationEmail) return;
    if (typeof onClose === "function") onClose();
    navigate("/verification", {
      state: { email: pendingVerificationEmail, operation: "verification" },
    });
  };

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-black/50 z-[1000]">
      <div className="relative bg-white p-10 rounded-xl shadow-2xl w-[90%] max-w-md font-sans">
        {errorMessage && (
          <div className="absolute top-2 left-1/2 -translate-x-1/2 bg-red-500 text-white opacity-95 px-4 py-2 rounded-lg shadow-md text-sm text-center max-w-[90%]">
            {errorMessage}
          </div>
        )}

        <button
          className="absolute -top-5 -left-5 bg-white border border-gray-300 rounded-full w-10 h-10 flex items-center justify-center shadow-md text-xl hover:bg-red-600 hover:text-white hover:border-transparent transition"
          onClick={onClose}
          aria-label="Close"
          type="button"
        >
          ×
        </button>

        <div className="w-full">
          <div className="mb-5 text-left leading-9">
            <h2 className="text-4xl font-normal text-[#23364c] mb-1">Welcome</h2>

            {selectedPlan?.title ? (
              <p className="text-sm text-[#8d8b9e] font-medium">
                Selected plan:{" "}
                <span className="font-semibold text-[#23364c]">
                  {selectedPlan.title} ({selectedPlan.price})
                </span>
              </p>
            ) : (
              <p className="text-base text-[#8d8b9e] font-medium">
                Fill in the form and wait for our contact.
              </p>
            )}
          </div>

          <form className="flex flex-col items-center gap-4" onSubmit={handleSubmit}>
            <div className="w-4/5">
              <input
                type="email"
                name="email"
                placeholder="Email"
                value={formData.email}
                onChange={handleChange}
                className="w-full py-3 px-5 rounded-full border border-gray-300 bg-[#f6f6f8] placeholder-gray-400 text-sm focus:outline-none focus:border-[#2ee56b]"
                required
              />
            </div>

            <div className="w-4/5">
              <input
                type="password"
                name="password"
                placeholder="Password"
                value={formData.password}
                onChange={handleChange}
                className="w-full py-3 px-5 rounded-full border border-gray-300 bg-[#f6f6f8] placeholder-gray-400 text-sm focus:outline-none focus:border-[#2ee56b]"
                required
              />
            </div>

            <div className="flex items-center space-x-2 mt-2 mb-2 w-4/5">
              <input
                id="stayLoggedIn"
                type="checkbox"
                name="stayLoggedIn"
                checked={formData.stayLoggedIn}
                onChange={handleChange}
                className="w-4 h-4"
              />
              <label htmlFor="stayLoggedIn" className="text-sm text-gray-600">
                Stay logged in
              </label>
            </div>

            <button
              type="submit"
              disabled={isSubmitting}
              className="w-4/5 bg-[#2ee56b] hover:bg-[#24c45a] disabled:opacity-60 disabled:cursor-not-allowed text-white font-bold py-3 rounded-full uppercase tracking-wide text-sm transition"
            >
              {isSubmitting ? "Logging in..." : "Login"}
            </button>

            <button
              type="button"
              onClick={handleForgotPassword}
              className="text-sm text-gray-500 text-center mt-2 underline hover:text-[#23364c]"
            >
              Forgot your password?
            </button>

            {pendingVerificationEmail && (
              <div className="w-4/5 rounded-xl border border-amber-200 bg-amber-50 p-4 text-left">
                <p className="text-sm font-semibold text-amber-900">Finish verifying your account</p>
                <p className="mt-1 text-sm text-amber-800 break-all">
                  We still have a pending verification for {pendingVerificationEmail}.
                </p>
                <button
                  type="button"
                  onClick={handleContinueVerification}
                  className="mt-3 rounded-full bg-[#23364c] px-4 py-2 text-sm font-semibold text-white hover:bg-[#1c2b3c] transition"
                >
                  Continue verification
                </button>
              </div>
            )}
          </form>

          <p className="text-sm text-gray-500 text-center mt-4">
            Don&apos;t have an account?{" "}
            <button
              type="button"
              onClick={() => {
                if (typeof onClose === "function") onClose();
                if (typeof onGoToSignUp === "function") onGoToSignUp();
              }}
              className="underline text-[#23364c] font-semibold hover:opacity-80"
            >
              Sign up
            </button>
          </p>

          <p className="text-xs text-[#716f85] text-center mt-3">
            By submitting the form, you agree to the Lotto GPT{" "}
            <button
              type="button"
              onClick={() => setIsPrivacyOpen(true)}
              className="underline text-[#23364c] font-semibold hover:opacity-80"
            >
              Privacy Policy
            </button>{" "}
            and{" "}
            <button
              type="button"
              onClick={() => setIsTermsOpen(true)}
              className="underline text-[#23364c] font-semibold hover:opacity-80"
            >
              Terms of Use
            </button>
            .
          </p>
        </div>
      </div>

      <TermsModal isOpen={isTermsOpen} onClose={() => setIsTermsOpen(false)} />
      <PrivacyPolicy isOpen={isPrivacyOpen} onClose={() => setIsPrivacyOpen(false)} />
    </div>
  );
};

export default LoginModal;